import React, { useCallback, useMemo, useState } from 'react';
import { Button, Input, Card, Loader, ConfirmacionDialog } from '@rfacil/shared/ui';
import { useForm, useNotificacionStore } from '@rfacil/shared/hooks';
import { validationUtils } from '@rfacil/shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { jwtDecode } from 'jwt-decode';
import { useChangePassword } from '../hooks/useUsuario';
import { useAuthStore } from '@rfacil/shared/services';

type FormValues = {
  contrasenaActual: string;
  nuevaContrasena: string;
  confirmarContrasena: string;
};

/**
 * Pantalla a pantalla completa: cambio de contraseña obligatorio tras login con NuevoIngreso.
 */
export const CambioPassword: React.FC = () => {
  const { showNotification } = useNotificacionStore();
  const changePassword = useChangePassword();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const token = useAuthStore((s) => s.token);
  const userEmail = useAuthStore((s) => s.user?.email);
  const pendingPasswordChange = useAuthStore((s) => s.pendingPasswordChange);
  const showVoluntaryPasswordChange = useAuthStore(
    (s) => s.showVoluntaryPasswordChange
  );
  const closeVoluntaryPasswordChange = useAuthStore(
    (s) => s.closeVoluntaryPasswordChange
  );
  const canDismissVoluntary =
    showVoluntaryPasswordChange && !pendingPasswordChange;

  /** Correo del JWT (mismo valor que guarda el login en user.email). */
  const correoSesion = useMemo(() => {
    const fromUser = userEmail?.trim() ?? '';
    if (fromUser) return fromUser;
    if (!token) return '';
    try {
      const decoded = jwtDecode<{ Correo?: string }>(token);
      return (decoded.Correo ?? '').trim();
    } catch {
      return '';
    }
  }, [userEmail, token]);

  const { values, errors, setValue, validateForm, isFormValid } = useForm<FormValues>(
    {
      contrasenaActual: '',
      nuevaContrasena: '',
      confirmarContrasena: '',
    },
    {
      contrasenaActual: (value: string) =>
        !value ? 'La contraseña actual es requerida' : null,
      nuevaContrasena: (value: string) =>
        !validationUtils.isValidPassword(value)
          ? 'La nueva contraseña debe tener al menos 8 caracteres'
          : null,
      confirmarContrasena: (value: string) =>
        !value ? 'Confirma la nueva contraseña' : null,
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!correoSesion) {
      showNotification(
        'No se pudo obtener el correo de la sesión. Cierra sesión e inicia de nuevo.',
        'error'
      );
      return;
    }

    if (!validateForm()) return;

    if (values.nuevaContrasena !== values.confirmarContrasena) {
      showNotification(
        'La nueva contraseña y la confirmación no coinciden.',
        'error'
      );
      return;
    }

    setConfirmationOpen(true);
  };

  const handleConfirm = useCallback(async () => {
    setConfirmationOpen(false);
    if (!correoSesion) return;
    try {
      await changePassword.mutateAsync({
        Correo: correoSesion,
        PSSActual: values.contrasenaActual,
        NuevaContrasenia: values.nuevaContrasena,
        ConfirmarContrasenia: values.confirmarContrasena,
      });
    } catch {
      // Errores ya notificados por el hook
    }
  }, [changePassword, correoSesion, values]);

  const loading = changePassword.isPending;

  const passwordMismatch =
    values.nuevaContrasena.length > 0 &&
    values.confirmarContrasena.length > 0 &&
    values.nuevaContrasena !== values.confirmarContrasena;

  const mismatchMessage = 'Las contraseñas no coinciden.';

  const inputInteractiveClass =
    'rounded-lg border-slate-200 bg-white py-2.5 text-slate-900 shadow-sm transition-all duration-200 ' +
    'placeholder:text-slate-400 hover:border-slate-300 hover:shadow ' +
    'focus:border-host-500 focus:ring-2 focus:ring-host-500/25 focus:ring-offset-0';

  return (
    <div
      className="fixed inset-0 z-[10050] flex min-h-screen flex-col overflow-y-auto bg-background-primary lg:flex-row"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cambio-contrasena-titulo"
    >
      {loading && (
        <Loader
          message="Actualizando contraseña..."
          subtitle="Por favor espera unos segundos"
          className="!z-[10060]"
        />
      )}

      {/* Misma columna izquierda que LoginForm (mismo fondo / gradiente) */}
      <aside className="hidden lg:flex lg:w-1/2 flex-shrink-0 items-center justify-center bg-gradient-to-br p-4">
        <div className="w-full">
          <img
            src="/LOGOSRFACIL_CONSLOGAN.svg"
            alt="Rfacil Suite"
            className="mx-auto h-auto w-full max-w-md object-contain"
          />
        </div>
      </aside>

      {/* Misma columna derecha que LoginForm: hereda bg-background-primary, sin blanco extra */}

      <div className="flex w-full flex-1 items-center justify-center p-5 lg:w-1/2">
        <div className="flex w-full max-w-md flex-col items-center">
          {/* Aparador: imagotipo encima de la card (referencia visual) */}
          <div className="mb-1 flex w-full flex-col items-center">
            <div className="mb-3 flex h-20 w-full max-w-[200px] items-center justify-center sm:h-24 sm:max-w-[220px]">
              <img
                src="/ICONO IMAGOTIPO.png"
                alt=""
                className="h-full w-full object-contain object-center"
              />
            </div>

          </div>

          <h1
            id="cambio-contrasena-titulo"
            className="mt-5 mb-2 text-center text-2xl font-bold tracking-tight text-[#1a365d]"
          >
            Cambiar contraseña
          </h1>
          <p className="mb-6 max-w-sm px-1 text-center text-sm leading-relaxed text-slate-500">
            Introduce tu contraseña actual y define una nueva. Deben coincidir la
            nueva y su confirmación.
          </p>

          <Card
            className="w-full !rounded-xl !border !border-slate-200 !bg-white !shadow-sm"
            padding="lg"
          >
          <form
            onSubmit={handleSubmit}
            className="text-left [&_label]:!font-semibold [&_label]:!text-blue-950"
          >
            <div className="mb-5">
              <Input
                label={
                  pendingPasswordChange
                    ? 'Contraseña actual (temporal)'
                    : 'Contraseña actual'
                }
                type="password"
                value={values.contrasenaActual}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue('contrasenaActual', e.target.value)
                }
                error={errors.contrasenaActual}
                required
                leftIcon={
                  <FontAwesomeIcon icon={faLock} className="text-slate-400" />
                }
                placeholder="Tu contraseña actual"
                className={inputInteractiveClass}
              />
            </div>

            <div className="mb-5">
              <Input
                label="Nueva contraseña"
                type="password"
                value={values.nuevaContrasena}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue('nuevaContrasena', e.target.value)
                }
                error={
                  errors.nuevaContrasena ??
                  (passwordMismatch ? mismatchMessage : undefined)
                }
                required
                leftIcon={
                  <FontAwesomeIcon icon={faLock} className="text-slate-400" />
                }
                placeholder="Mínimo 8 caracteres"
                className={inputInteractiveClass}
              />
            </div>

            <div className="mb-6">
              <Input
                label="Confirmación de nueva contraseña"
                type="password"
                value={values.confirmarContrasena}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue('confirmarContrasena', e.target.value)
                }
                error={
                  errors.confirmarContrasena ??
                  (passwordMismatch ? mismatchMessage : undefined)
                }
                required
                leftIcon={
                  <FontAwesomeIcon icon={faLock} className="text-slate-400" />
                }
                placeholder="Repite la nueva contraseña"
                className={inputInteractiveClass}
              />
            </div>

            <p className="mb-5 text-xs leading-relaxed text-slate-500">
              La contraseña debe cumplir las reglas de seguridad indicadas por el
              sistema. Si hay requisitos adicionales, el servidor te mostrará el
              detalle al guardar.
            </p>

            <Button
              type="submit"
              variant="ghost"
              fullWidth
              size="lg"
              disabled={!correoSesion || !isFormValid()}
              className="rounded-lg !bg-slate-500 !text-white py-3 text-[15px] font-semibold shadow-md transition-all duration-200 hover:!bg-slate-600 hover:shadow-lg active:scale-[0.99] focus:!ring-2 focus:!ring-slate-400 focus:!ring-offset-2 disabled:!opacity-50 disabled:active:scale-100"
            >
              Guardar contraseña
            </Button>

            {canDismissVoluntary ? (
              <p className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => closeVoluntaryPasswordChange()}
                  className="inline-flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-sm font-medium text-host-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-host-500 focus-visible:ring-offset-2 rounded-sm"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
                  Volver
                </button>
              </p>
            ) : null}
          </form>
          </Card>
        </div>
      </div>

      <ConfirmacionDialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={() => {
          void handleConfirm();
        }}
        zIndex={12000}
        title="Confirmar cambio de contraseña"
        subtitle="Verifica los datos antes de continuar"
        message={
          <>
            ¿Estás seguro de que deseas{' '}
            <span className="font-semibold text-slate-900">
              actualizar tu contraseña
            </span>
            ? No podrás deshacer esta acción desde aquí.
          </>
        }
        confirmText="Confirmar"
        cancelText="Cancelar"
        loading={loading}
      />
    </div>
  );
};

export default CambioPassword;
